import { supabase } from '../config/supabase';
import { STORAGE_BUCKETS } from '../config/storageBuckets';
import { resolveUploadedMainImage } from '../utils/patternFields';

const uploadedFileUrls = new WeakMap();

const PUBLIC_PATTERN_FIELDS = [
    'id',
    'title',
    'slug',
    'description',
    'price',
    'previous_price',
    'status',
    'difficulty_level',
    'skill_level',
    'yarn_weight',
    'yarn_weights',
    'needles',
    'construction',
    'languages',
    'categories',
    'category',
    'size_tag',
    'materials',
    'video_url',
    'images',
    'main_image',
    'pdf_version',
    'pdf_date',
    'changelog',
].join(',');

function reportProgress(onProgress, completed, total, message) {
    if (!onProgress) return;
    onProgress({
        completed,
        total,
        percent: total === 0 ? 100 : Math.round((completed / total) * 100),
        message
    });
}

export const patternService = {
    /**
     * Uploads a file to Supabase storage with a random UUID name for security
     * @param {File} file The file to upload
     * @param {string} bucket The bucket name (patterns, images)
     * @returns {Promise<string|{path: string, name: string}>} A public image URL or private PDF reference
     */
    async uploadFile(file, bucket) {
        const cachedUploads = uploadedFileUrls.get(file);
        if (cachedUploads?.has(bucket)) {
            return cachedUploads.get(bucket);
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from(bucket)
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const uploadedValue = bucket === STORAGE_BUCKETS.PATTERN_PDFS
            ? { path: filePath, name: file.name }
            : supabase.storage.from(bucket).getPublicUrl(filePath).data.publicUrl;

        const uploadsByBucket = cachedUploads || new Map();
        uploadsByBucket.set(bucket, uploadedValue);
        uploadedFileUrls.set(file, uploadsByBucket);

        return uploadedValue;
    },

    /**
     * Creates or updates a pattern with multiple images and localized PDFs
     */
    async createPattern(fullData, onProgress) {
        try {
            const { images, pdf_files, main_image, ...patternData } = fullData;
            const uploadCount = images.filter((image) => image instanceof File).length
                + pdf_files.filter((pdfItem) => pdfItem.file).length;
            const totalSteps = uploadCount + 1;
            let completedSteps = 0;

            reportProgress(
                onProgress,
                completedSteps,
                totalSteps,
                uploadCount > 0 ? 'Preparing file uploads…' : 'Saving pattern…'
            );

            const uploadWithProgress = async (file, bucket, label) => {
                const url = await this.uploadFile(file, bucket);
                completedSteps += 1;
                reportProgress(onProgress, completedSteps, totalSteps, `${label} uploaded`);
                return url;
            };

            // 1. Upload Images and map URLs
            const uploadedImageUrls = await Promise.all(
                images.map(async (img) => {
                    if (img instanceof File) {
                        return await uploadWithProgress(img, STORAGE_BUCKETS.PATTERN_IMAGES, 'Image');
                    }
                    return img; // Already a URL
                })
            );

            // 2. Map Main Image (if it was a file, find its new URL)
            const finalMainImage = resolveUploadedMainImage(images, uploadedImageUrls, main_image);

            // 3. Upload PDFs with language metadata
            const finalPdfFiles = await Promise.all(
                pdf_files.map(async (pdfItem) => {
                    if (pdfItem.file) {
                        const uploadedPdf = await uploadWithProgress(
                            pdfItem.file,
                            STORAGE_BUCKETS.PATTERN_PDFS,
                            `${pdfItem.language || 'Pattern'} PDF`
                        );
                        return {
                            path: uploadedPdf.path,
                            name: uploadedPdf.name,
                            language: pdfItem.language,
                        };
                    }
                    return {
                        path: pdfItem.path,
                        name: pdfItem.name,
                        language: pdfItem.language,
                        ...(pdfItem.url ? { url: pdfItem.url } : {}),
                    };
                })
            );

            // 4. Map the first PDF to pdf_url for backward compatibility (optional but safe)
            const primaryPdfUrl = '';

            // 5. Build Final Payload
            const yarnWeights = Array.isArray(patternData.yarn_weights)
                ? patternData.yarn_weights.filter(Boolean)
                : [];
            const needles = Array.isArray(patternData.needles)
                ? patternData.needles.filter(Boolean)
                : [];
            const payload = {
                ...patternData,
                status: patternData.status === 'published' ? 'published' : 'draft',
                price: patternData.price === '' ? null : Number(patternData.price),
                previous_price: patternData.previous_price === '' ? null : Number(patternData.previous_price),
                skill_level: patternData.difficulty_level || 'Principiante',
                category: patternData.category || patternData.categories?.[0] || 'Other',
                yarn_weights: yarnWeights,
                yarn_weight: yarnWeights[0] || patternData.yarn_weight || 'DK',
                needles,
                pdf_date: patternData.pdf_date || null,
                images: uploadedImageUrls,
                main_image: finalMainImage || uploadedImageUrls[0],
                pdf_files: finalPdfFiles,
                pdf_url: primaryPdfUrl, // Keep for legacy if needed
                updated_at: new Date()
            };

            // Remove temporary UI fields
            delete payload.pdf; // Old field

            // 6. Save to database (Upsert)
            reportProgress(onProgress, completedSteps, totalSteps, 'Saving pattern details…');
            const { data, error } = await supabase
                .from('patterns')
                .upsert([payload])
                .select()
                .single();

            if (error) throw error;
            reportProgress(onProgress, totalSteps, totalSteps, 'Pattern saved');
            return data;
        } catch (error) {
            console.error('Error in createPattern:', error);
            throw error;
        }
    },

    /**
     * Fetches all patterns
     */
    async getPatterns() {
        const { data, error } = await supabase
            .from('patterns')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    /**
     * Fetches only customer-visible patterns.
     */
    async getPublishedPatterns() {
        const { data, error } = await supabase
            .from('patterns')
            .select(PUBLIC_PATTERN_FIELDS)
            .eq('status', 'published')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    /**
     * Fetches a single pattern by slug
     */
    async getPatternBySlug(slug) {
        const { data, error } = await supabase
            .from('patterns')
            .select(PUBLIC_PATTERN_FIELDS)
            .eq('slug', slug)
            .eq('status', 'published')
            .single();

        if (error) throw error;
        return data;
    },

    /**
     * Deletes a pattern and its associated files
     */
    async deletePattern(id, pdfUrl, imageUrls, pdfFiles = []) {
        // 1. Delete record from database
        const { error: dbError } = await supabase
            .from('patterns')
            .delete()
            .eq('id', id);

        if (dbError) throw dbError;

        // 2. Cleanup Storage (Best effort)
        try {
            // Cleanup PDF files (new structure)
            if (pdfFiles && pdfFiles.length > 0) {
                const pdfPaths = pdfFiles
                    .map(p => p.path || p.url?.split('/').pop())
                    .filter(Boolean);
                await supabase.storage.from(STORAGE_BUCKETS.PATTERN_PDFS).remove(pdfPaths);
            } else if (pdfUrl) {
                // Legacy cleanup
                const pdfPath = pdfUrl.split('/').pop();
                await supabase.storage.from(STORAGE_BUCKETS.PATTERN_PDFS).remove([pdfPath]);
            }

            // Cleanup Images
            if (imageUrls && imageUrls.length > 0) {
                const imagePaths = imageUrls.map(url => url.split('/').pop());
                await supabase.storage.from(STORAGE_BUCKETS.PATTERN_IMAGES).remove(imagePaths);
            }
        } catch (storageError) {
            console.warn('Failed to cleanup storage files:', storageError);
        }
    }
};
